import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { namespace } from '@common-config/constants';
import { backendApi, type McpPreset, type McpPresetQuery, type MyBridgeAgent } from '@common/reducers/backend-api/backend-api';
import { ArrowLeft, BadgeCheck, Clock, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { KeyField, McpLogo, PRESET_GRID, PresetCard, PresetSearch, useCategoryLabel, useDebouncedValue, useMcpConnect } from './McpServersSection';
import { agentEditPath } from './workspace-view';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/** How many cards a category section shows before "Show all". */
const SECTION_SIZE = 6;
/** The categories shown as sections on the landing view (the rest are one chip away). */
const SECTION_COUNT = 8;
const PAGE_SIZE = 60;

/**
 * The integrations directory — a page of its own (`/agents/integrations`), laid out like the
 * Claude directory: search, categories, sections; a card opens the integration's details and
 * adds it to one of the user's agents (`?agent=` preselects one). Connecting reuses the agent
 * form's flow (popup sign-in → the agent's tools are then ticked in its form).
 */
export const IntegrationsDirectory: React.FC<{ agents: MyBridgeAgent[]; isRtl: boolean; backLabel: string }> = ({ agents, isRtl, backLabel }) => {
  const { t } = useTranslation(namespace);
  const label = useCategoryLabel();
  const [params] = useSearchParams();
  const [text, setText] = useState('');
  const q = useDebouncedValue(text);
  const [category, setCategory] = useState('');
  const [open, setOpen] = useState<McpPreset | null>(null);
  const { data: categories = [] } = backendApi.useGetMcpCategoriesQuery();

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-8 pt-8 sm:pt-10 pb-28" data-testid="mcp-directory">
      <div className="flex items-center gap-2 mb-1">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to="/agents" aria-label={backLabel}>
            <ArrowLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
          </Link>
        </Button>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.035em] text-ai-text">{t('myBridge.mcp.directoryTitle')}</h1>
        <GuideVideoButton lesson="agent-integrations" variant="icon" surface="agents_integrations_header" />
      </div>
      <p className="mb-6 text-sm text-ai-text-secondary">{t('myBridge.mcp.directorySubtitle')}</p>

      <div className="mb-8">
        <PresetSearch text={text} onText={setText} category={category} onCategory={setCategory} inputClassName="h-11" testId="mcp-directory-search" />
      </div>

      {!q && !category ? (
        <div className="space-y-10">
          {/* The same query (and cache entry) as the agent form's library. */}
          <Section title={t('myBridge.mcp.popular')} query={{ q: '', category: '' }} onOpen={setOpen} />
          {categories.slice(0, SECTION_COUNT).map((c) => (
            <Section key={c.id} title={label(c.id)} count={c.count} query={{ q: '', category: c.id, limit: SECTION_SIZE }} onOpen={setOpen} onShowAll={() => setCategory(c.id)} />
          ))}
        </div>
      ) : (
        <Results key={`${q}|${category}`} q={q} category={category} onOpen={setOpen} />
      )}

      <IntegrationDialog preset={open} agents={agents} defaultAgentId={params.get('agent') ?? ''} onClose={() => setOpen(null)} />
    </div>
  );
};

/** One landing section: a titled grid of a query's cards, with "Show all". */
const Section: React.FC<{ title: string; count?: number; query: McpPresetQuery; onOpen: (p: McpPreset) => void; onShowAll?: () => void }> = ({
  title,
  count,
  query,
  onOpen,
  onShowAll,
}) => {
  const { t } = useTranslation(namespace);
  const { data = [], isLoading } = backendApi.useGetMcpPresetsQuery(query);
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-ai-text">
          {title}
          {count !== undefined && <span className="ms-2 rounded-ai-pill bg-ai-muted px-2 py-0.5 text-xs font-normal text-ai-text-secondary">{count}</span>}
        </h2>
        {onShowAll && (
          <button type="button" onClick={onShowAll} className="text-sm font-medium text-ai-text hover:underline underline-offset-4">
            {t('myBridge.mcp.showAll')} →
          </button>
        )}
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-ai-text-secondary" /> : <Cards presets={data} onOpen={onOpen} />}
    </section>
  );
};

/** A search / category result; "Load more" asks for a larger list (the server caps it). */
const Results: React.FC<{ q: string; category: string; onOpen: (p: McpPreset) => void }> = ({ q, category, onOpen }) => {
  const { t } = useTranslation(namespace);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data = [], isFetching } = backendApi.useGetMcpPresetsQuery({ q, category, limit });
  if (isFetching && data.length === 0) return <Loader2 className="h-5 w-5 animate-spin text-ai-text-secondary" />;
  if (data.length === 0) return <p className="text-sm text-ai-text-secondary">{t('myBridge.mcp.noResults')}</p>;
  return (
    <div className="space-y-4">
      <Cards presets={data} onOpen={onOpen} />
      {data.length === limit && (
        <Button variant="outline" onClick={() => setLimit((n) => n + PAGE_SIZE)} disabled={isFetching} data-testid="mcp-directory-load-more">
          {isFetching && <Loader2 className="me-1.5 h-4 w-4 animate-spin" />}
          {t('myBridge.mcp.loadMore')}
        </Button>
      )}
    </div>
  );
};

/** Cards open the details — "coming soon" ones too (adding is decided there). */
const Cards: React.FC<{ presets: McpPreset[]; onOpen: (p: McpPreset) => void }> = ({ presets, onOpen }) => (
  <div className={PRESET_GRID}>
    {presets.map((p) => (
      <PresetCard key={p.id} preset={p} opensDetails onPick={() => onOpen(p)} />
    ))}
  </div>
);

/** An integration's details + "Add to agent". */
const IntegrationDialog: React.FC<{ preset: McpPreset | null; agents: MyBridgeAgent[]; defaultAgentId: string; onClose: () => void }> = ({ preset: p, agents, defaultAgentId, onClose }) => {
  const { t } = useTranslation(namespace);
  const label = useCategoryLabel();
  const [agentId, setAgentId] = useState('');
  const [done, setDone] = useState<string | null>(null);
  // The completed sign-in names its own agent (the selection may have changed meanwhile).
  const connect = useMcpConnect(agentId, setDone);
  useEffect(() => {
    setAgentId(agents.some((a) => a.id === defaultAgentId) ? defaultAgentId : agents[0]?.id ?? '');
    setDone(null);
    connect.setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset per opened integration only
  }, [p?.id]);
  if (!p) return null;
  const soon = p.available === false;
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px] max-h-[86vh] overflow-y-auto" data-testid="mcp-directory-dialog">
        <div className="flex items-start gap-3">
          <McpLogo src={p.icon} size={52} />
          <div className="min-w-0">
            <DialogTitle className="flex items-center gap-1.5 text-lg">
              {p.name}
              {p.verified && <BadgeCheck className="h-4 w-4 text-ai-text-secondary" aria-label={t('myBridge.mcp.verified')} />}
            </DialogTitle>
            {p.by && <p className="text-xs text-ai-text-secondary">{t('myBridge.mcp.by', { by: p.by })}</p>}
          </div>
        </div>
        <DialogDescription className="whitespace-pre-line text-sm text-ai-text">{p.description || p.summary}</DialogDescription>
        {!!p.categories?.length && (
          <div className="flex flex-wrap gap-1.5">
            {p.categories.map((c) => (
              <span key={c} className="rounded-ai-pill bg-ai-muted px-2.5 py-0.5 text-xs text-ai-text-secondary">
                {label(c)}
              </span>
            ))}
          </div>
        )}
        {!!p.tools?.length && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-ai-text-secondary">{t('myBridge.mcp.toolsTitle')}</div>
            <div className="flex flex-wrap gap-1">
              {p.tools.map((tool) => (
                <span key={tool} className="rounded bg-ai-muted px-1.5 py-0.5 font-mono text-[11px] text-ai-text" dir="ltr">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 rounded-ai-md border border-ai-border p-3">
          {soon ? (
            <p className="flex items-start gap-2 text-sm text-ai-text-secondary" data-testid="mcp-directory-coming-soon">
              <Clock className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <b>{t('myBridge.mcp.comingSoon')}</b> — {t('myBridge.mcp.comingSoonHint')}
              </span>
            </p>
          ) : agents.length === 0 ? (
            <p className="text-sm text-ai-text-secondary">{t('myBridge.mcp.noAgents')}</p>
          ) : done ? (
            <p className="text-sm text-ai-text" data-testid="mcp-directory-added">
              ✓ {t('myBridge.mcp.added')} —{' '}
              <Link to={`${agentEditPath(done)}?view=form`} className="font-medium underline underline-offset-4">
                {t('myBridge.mcp.openAgent')}
              </Link>
            </p>
          ) : (
            <div className="space-y-2">
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger className="min-w-[200px] flex-1" data-testid="mcp-directory-agent">
                  <SelectValue placeholder={t('myBridge.mcp.chooseAgent')} />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {p.auth === 'key' ? (
                <KeyField name={p.name} helpUrl={p.keyHelpUrl} busy={connect.busy || !agentId} submitLabel={t('myBridge.mcp.addToAgent')} onSubmit={(key) => void connect.addServer(p.id, key)} />
              ) : (
                <Button disabled={!agentId || connect.busy} onClick={() => void connect.addServer(p.id)} data-testid="mcp-directory-add">
                  {connect.busy && <Loader2 className="me-1.5 h-4 w-4 animate-spin" />}
                  {t('myBridge.mcp.addToAgent')}
                </Button>
              )}
            </div>
          )}
          {connect.error && <p className="mt-2 text-sm text-red-500">{connect.error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
